import { useState, useEffect, useCallback } from 'react';
import { Loader2, Inbox, RefreshCw } from 'lucide-react';
import { api } from './api';
import MailItem from './MailItem';

export default function MailsTab({ leadId, onCounts, refreshKey, onReply }) {
  const [data, setData] = useState({ mails: [], counts: { total: 0, workflow_outbound: 0, manual_outbound: 0, inbound_reply: 0, unread_inbound: 0 } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api(`/api/sales/leads/${leadId}/mails`);
      setData(res);
      onCounts?.(res.counts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [leadId, onCounts]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleMarkedRead = (replyId) => {
    setData((prev) => ({
      ...prev,
      mails: prev.mails.map((m) => (m.reply_id === replyId || (m.source_type === 'inbound_reply' && m.id === replyId) ? { ...m, is_read: true } : m)),
      counts: { ...prev.counts, unread_inbound: Math.max(0, (prev.counts.unread_inbound || 0) - 1) },
    }));
    onCounts?.({ ...data.counts, unread_inbound: Math.max(0, (data.counts.unread_inbound || 0) - 1) });
  };

  const filtered = filter === 'all'
    ? data.mails
    : filter === 'inbound'
      ? data.mails.filter((m) => m.direction === 'in')
      : filter === 'outbound'
        ? data.mails.filter((m) => m.direction === 'out')
        : filter === 'workflow'
          ? data.mails.filter((m) => m.source_type === 'workflow_outbound')
          : filter === 'needs_reply'
            ? data.mails.filter((m) => m.needs_reply && !m.is_done)
            : filter === 'unread'
              ? data.mails.filter((m) => m.direction === 'in' && !m.is_read && !m.is_done)
              : data.mails;

  const FilterPill = ({ id, label, count, subset, title }) => (
    <button
      onClick={() => setFilter(id)}
      title={title}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${subset ? 'ml-1' : ''} ${filter === id
        ? 'bg-[#338edc] text-white border-[#338edc]'
        : subset
          ? 'bg-[var(--bg-inset)] text-[var(--text-tertiary)] border-dashed border-[var(--border-card)] hover:bg-[var(--bg-card)]'
          : 'bg-[var(--bg-card)] text-[var(--text-secondary)] border-[var(--border-card)] hover:bg-[var(--bg-inset)]'
        }`}
    >
      {subset && <span className="opacity-60 mr-0.5">↳</span>}
      {label} {count != null && <span className="opacity-70">({count})</span>}
    </button>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <FilterPill id="all" label="Alle" count={data.counts.total} />
        <FilterPill id="unread" label="Ungelesen" count={data.counts.unread_inbound} />
        <FilterPill id="needs_reply" label="Zu beantworten" count={data.counts.needs_reply || 0} />
        <FilterPill id="inbound" label="Eingehend" count={(data.counts.inbound_reply || 0) + (data.counts.manual_inbound || 0)} />
        <FilterPill id="outbound" label="Ausgehend" count={data.counts.workflow_outbound + data.counts.manual_outbound} />
        {/* #1047 Bug 4 Option A — Workflow als Subset von Ausgehend, optisch eingerückt + dashed border + Tooltip */}
        <FilterPill id="workflow" label="Workflow" count={data.counts.workflow_outbound} subset title="Subset von Ausgehend — nur Workflow-Mails" />
        <button
          onClick={load}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] rounded disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {loading && data.mails.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-[var(--text-tertiary)]">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-[13px] text-red-600 px-3 py-2 bg-red-50 rounded">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-[var(--text-tertiary)]">
          <Inbox className="w-8 h-8 mb-2 opacity-50" />
          <span className="text-[13px]">Keine E-Mails</span>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <MailItem
              key={`${m.source_type}-${m.id}`}
              mail={m}
              onMarkedRead={handleMarkedRead}
              onChanged={load}
              onReply={onReply}
            />
          ))}
        </div>
      )}
    </div>
  );
}
